import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FormDialog } from "@/components/ui/form-dialog";
import { FormSection } from "@/components/ui/form-section";
import { Tag } from "lucide-react";
import { IconPicker } from "./IconPicker";
import { useUpdateCategory } from "@/hooks/useCategories";
import { toast } from "sonner";
import type { Category } from "@/types";

interface CategoryEditDialogProps {
  category: Category | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function CategoryEditForm({
  category,
  open,
  onOpenChange,
}: {
  category: Category;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const updateCategory = useUpdateCategory();

  const initialHex = category.colorHex.startsWith("#")
    ? category.colorHex.slice(1)
    : category.colorHex;
  const [form, setForm] = useState({
    name: category.name,
    iconName: category.iconName,
    colorHex: initialHex,
    sortOrder: String(category.sortOrder),
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await updateCategory.mutateAsync({
        id: category.id,
        body: {
          name: form.name,
          iconName: form.iconName,
          colorHex: form.colorHex.replace("#", ""),
          sortOrder: parseInt(form.sortOrder) || 99,
        },
      });
      toast.success("Kategoria zaktualizowana");
      onOpenChange(false);
    } catch (err) {
      toast.error("Nie udało się zaktualizować kategorii", {
        description: err instanceof Error ? err.message : undefined,
      });
    }
  };

  const previewHex = `#${form.colorHex.replace("#", "")}`;

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Edytuj kategorię"
      subtitle={category.id}
      size="sm"
      footer={
        <>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Anuluj
          </Button>
          <Button type="submit" form="category-edit-form" disabled={updateCategory.isPending}>
            {updateCategory.isPending ? "Zapisywanie..." : "Zapisz zmiany"}
          </Button>
        </>
      }
    >
      <form id="category-edit-form" onSubmit={handleSubmit}>
        <FormSection icon={Tag} title="Kategoria">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Nazwa</Label>
              <Input
                id="edit-name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Ikona</Label>
              <IconPicker
                value={form.iconName}
                onChange={(iconName) => setForm((f) => ({ ...f, iconName }))}
                colorHex={form.colorHex}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-colorHex">Kolor (hex)</Label>
              <div className="flex items-center gap-2">
                <div
                  className="h-8 w-8 shrink-0 rounded border"
                  style={{ backgroundColor: previewHex }}
                />
                <Input
                  id="edit-colorHex"
                  value={form.colorHex}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, colorHex: e.target.value.replace("#", "") }))
                  }
                  placeholder="1E5A9F"
                  maxLength={7}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-sortOrder">Kolejność sortowania</Label>
              <Input
                id="edit-sortOrder"
                type="number"
                value={form.sortOrder}
                onChange={(e) => setForm((f) => ({ ...f, sortOrder: e.target.value }))}
                min="0"
              />
            </div>
          </div>
        </FormSection>
      </form>
    </FormDialog>
  );
}

export function CategoryEditDialog({ category, open, onOpenChange }: CategoryEditDialogProps) {
  if (!category) return null;
  return (
    <CategoryEditForm
      key={category.id}
      category={category}
      open={open}
      onOpenChange={onOpenChange}
    />
  );
}
